import clsx from 'clsx';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';

import { ActionCloseIcon } from '../../assets/icons';
import { Button } from '../Button';
import { Text } from '../Text';

import { createPortal } from 'react-dom';
import { closeBtn, modal, modalContents, modalFooter, modalHeader, modalInnerContents, modalTags } from './modal.css';

const testId = {
  closeButton: 'ui-components-modal-close-button',
  modalTitle: 'ui-component-modal-title',
};

type Props = {
  children: React.ReactNode;
};

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

const ModalBase = ({ isOpen: open, onClose: onCloseHandler, children, className }: ModalProps) => {
  // TODO: implement animations using dialogState.
  const [dialogState, setDialogState] = useState<'open' | 'closing' | 'closed'>('closed');
  const dialogRef = useRef<HTMLDialogElement>(null);

  useMemo(() => {
    if (open && !dialogRef?.current?.open) {
      dialogRef?.current?.showModal();
      setDialogState('open');
    }
  }, [open]);

  const onCloseFinish = useCallback(() => {
    dialogRef?.current?.close();
    setDialogState('closed');
  }, []);

  const onClose = useCallback(() => {
    onCloseHandler();
    setDialogState('closing');
    setTimeout(() => {
      onCloseFinish();
    }, 250);
  }, [onCloseFinish, onCloseHandler]);

  const onEsc = useCallback(
    (e: ChangeEvent<HTMLDialogElement>) => {
      e.preventDefault();
      onClose();
    },
    [onClose],
  );

  const onClick = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: TODO: fix
    ({ target }: any) => {
      const { current: el } = dialogRef;
      if (target === el) {
        onClose();
      }
    },
    [onClose],
  );

  return createPortal(
    // Disable rules as they do not lint dialog correctly; onCancel handles user Escape key press as required:
    // https://stackoverflow.com/questions/56714318/how-to-disable-multiple-rules-for-eslint-nextline.

    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    // biome-ignore lint/a11y/useKeyWithClickEvents: TODO: fix
    <dialog
      ref={dialogRef}
      onClick={onClick}
      onCancel={onEsc}
      className={clsx(modal, className)}
      data-state={dialogState}
    >
      <Button
        type="button"
        className={closeBtn}
        onClick={onEsc}
        aria-label="Close modal"
        icon={ActionCloseIcon}
        iconSize="large"
        variant="tertiary"
        data-testid={testId.closeButton}
      />
      <div className={modalContents}>{children}</div>
    </dialog>,
    document.body,
  );
};

const ModalHeader = ({ children }: Props) => (
  <div className={modalHeader} data-testid={testId.modalTitle}>
    {children}
  </div>
);

const ModalTitle = ({ children }: Props) => <Text variant="page-title">{children}</Text>;

const ModalSubtitle = ({ children }: Props) => (
  <Text variant="page-subtitle" data-testid="modal-subtitle">
    {children}
  </Text>
);

const ModalTags = ({ children }: Props) => (
  <div className={modalTags} data-testid="modal-tags">
    {children}
  </div>
);

const ModalMeta = ({ children }: Props) => (
  <Text variant="meta" data-testid="modal-meta">
    {children}
  </Text>
);

const ModalContents = ({ children }: Props) => <div className={modalInnerContents}>{children}</div>;

const ModalFooter = ({ children }: Props) => <div className={modalFooter}>{children}</div>;

export const Modal = Object.assign(ModalBase, {
  Header: ModalHeader,
  Title: ModalTitle,
  Subtitle: ModalSubtitle,
  Tags: ModalTags,
  Meta: ModalMeta,
  Contents: ModalContents,
  Footer: ModalFooter,
});
